// Statistics.tsx
// Pantalla de estadísticas con lógica completa en un solo fichero.

import React, { useState, useEffect, useRef } from 'react'
import moment from 'moment'
import { getFirestore, collection, query, where, getDocs, Query } from 'firebase/firestore'
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip,CartesianGrid, Legend,LabelList, ResponsiveContainer } from 'recharts'
import { sendQuery } from 'src/services/chatService'
import { aggregateByCategory } from './statsUtils'
import { Card } from '@mui/material'
import {useTheme} from '@mui/material/styles'
import { Booking } from 'src/types/pages.types'

// 1. Definición de tipos
interface ChartSpec {
  chartType: 'pie' | 'line' | 'bar'
  metric: 'Sesiones' | 'Tiempo'
  dimension: 'month' | 'day' | 'affectedZone' | 'completed' | 'process' | 'gender' | 'store' | 'sector' | 'role'
  filters: {
    gender?: string
    ageMin?: number
    ageMax?: number
    storeId?: string
    companyId?: string
    completed?: boolean
    processId?: string
    sector?: string
    role?: string
    [key: string]: any
  }
  period?: { from: string; to: string }
  description?: string // Descripción amigable del gráfico
}

// 2. Función para consultar Firestore con filtros
async function fetchBookingsBySpec(spec: ChartSpec): Promise<Booking[]> {
  const db = getFirestore()
  let q: Query = collection(db, 'bookings')

  // Aplicar rango de fechas si existe
  if (spec.period) {
    q = query(
      q,
      where('date', '>=', spec.period.from),
      where('date', '<=', spec.period.to)
    )
  }
  const f = spec.filters ||{}
  console.log(f)
  if (f?.companyId) q = query(q, where('companyId', '==', f?.companyId))
  if (f?.store)   q = query(q, where('store.name', '==', f?.store))
  if (f?.completed != null) q = query(q, where('completed', '==', f?.completed))
  if (f?.gender)    q = query(q, where('patient.all.gender.id', '==', f?.gender))
  if (f?.affectedZone) q = query(q, where('affectedZone.name', '==', f?.affectedZone))
  if (f?.sector) q = query(q, where('sector.name', '==', f?.sector))
  if (f?.role) q = query(q, where('patient.all.role.name', '==', f?.role))
  if (f?.processId) {
    // Filtrar tanto en campaign como en incident
    const q1 = query(q, where('campaign.all.process.id', '==', f?.processId))
    const q2 = query(q, where('incident.all.process.id', '==', f?.processId))
    console.log(q1, q2)
    // Ejecutar ambas y concatenar
    const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)])
    const docs = [...snap1.docs, ...snap2.docs]
    return docs.map(d => d.data() as Booking)
  }

  // Filtros de edad con rango en campo birthday
  if (f.ageMin != null || f.ageMax != null) {
    const clauses: any[] = []
    if (f.ageMin != null) {
      const maxBirthday = moment().subtract(f.ageMin, 'years').format('YYYY-MM-DD')
      clauses.push(where('patient.all.birthday', '<=', maxBirthday))
    }
    if (f.ageMax != null) {
      const minBirthday = moment().subtract(f.ageMax, 'years').format('YYYY-MM-DD')
      clauses.push(where('patient.all.birthday', '>=', minBirthday))
    }
    if (clauses.length) q = query(q, ...clauses)
  }

  // Ejecutar consulta
  console.log(q)
  const snap = await getDocs(q)
  return snap.docs.map(d => d.data() as Booking)
}

// 2. Utilidades de estadística
// function filterBookings(bookings: Booking[], filters: ChartSpec['filters']): Booking[] {
//   console.log(bookings, filters)
//   return bookings.filter((b) =>
//     Object.entries(filters).every(([key, val]) => {
//       if (val == null) return true
//       switch (key) {
//         case 'gender':
//           return b.patient.all.gender.id === (val as string)
//         case 'ageMin':
//           return moment().diff(moment(b.patient.all.birthday), 'years') >= Number(val)
//         case 'ageMax':
//           return moment().diff(moment(b.patient.all.birthday), 'years') <= Number(val)
//         case 'storeId':
//           return b.storeId === (val as string)
//         case 'companyId':
//           return b.companyId === (val as string)
//         case 'completed':
//           return b.completed === Boolean(val)
//         case 'processId':
//           return b.campaign?.all.process.id === val || b.incident?.all.process.id === val
//         default:
//           return true
//       }
//     })
//   )
// }

function aggregateTimeSeries(bookings: Booking[], spec: ChartSpec): Array<{ [key: string]: number }> {
  const { period, dimension } = spec

  // Inicializar buckets
  let buckets: any[] = []
  if (dimension === 'month') {
    buckets = Array.from({ length: 12 }, (_, i) => ({ month: i + 1, Sesiones: 0, Tiempo: 0 }))
  } else if (dimension === 'day') {
    buckets = Array.from({ length: 31 }, (_, i) => ({ day: i + 1, Sesiones: 0, Tiempo: 0 }))
  } else {
    return []
  }

  bookings
    .filter((b) => {
      if (!period) return true
      const d = moment(b.date, 'YYYY-MM-DD')
      return d.isBetween(moment(period.from), moment(period.to), 'day', '[]')
    })
    .forEach((b) => {
      const idx = dimension === 'month' ? moment(b.date, 'YYYY-MM-DD').month() : moment(b.date, 'YYYY-MM-DD').date() - 1
      buckets[idx].Sesiones += 1
      buckets[idx].Tiempo += Number(b.minutes)
    })

  return buckets
}

function aggregatePie(bookings: Booking[], spec: ChartSpec): Array<{ name: string; value: number }> {
  const counter = new Map<string, number>()

  bookings.forEach((b) => {
    let key = 'Otros'
    switch (spec.dimension) {
      case 'affectedZone':
        key = b.affectedZone.name
        break
      case 'completed':
        key = b.completed ? 'Asiste' : 'No asiste'
        break
      case 'process':
        key = b.campaign?.all.process.name || b.incident?.all.process.name || 'Desconocido'
        break
      case 'gender':
        key = b.patient.all.gender.name
        break
      default:
        key = 'Otros'
    }
    counter.set(key, (counter.get(key) || 0) + 1)
  })

  return Array.from(counter.entries()).map(([name, value]) => ({ name, value }))
}

// // 3. Fetch desde Firestore
// async function fetchAllBookings(): Promise<Booking[]> {
//   const db = getFirestore()
//   const snap = await getDocs(collection(db, 'bookings'))
//   return snap.docs.map((doc) => doc.data() as Booking)
// }

// 4. ChatPanel - WhatsApp Style
interface ChatPanelProps {
  onNewChart: (spec: ChartSpec) => void
}

interface ChatMessage {
  from: 'user' | 'bot'
  text: string
  timestamp: Date
  id: string
}

// Send Icon Component
const SendIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" fill="currentColor"/>
  </svg>
)

// Microphone Icon Component
const MicrophoneIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" fill="currentColor"/>
    <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" fill="currentColor"/>
  </svg>
)

function ChatPanel({ onNewChart }: ChatPanelProps) {
  const [msgs, setMsgs] = useState<ChatMessage[]>([
    { 
      from: 'bot', 
      text: '¡Hola! Soy tu asistente de estadísticas. ¿Qué datos te gustaría visualizar? Puedes preguntarme sobre sesiones, tiempo, tiendas, zonas afectadas, y más.', 
      timestamp: new Date(),
      id: '1'
    },
    {
      from: 'bot',
      text: '💡 Tip: Puedes preguntar cosas como "Muéstrame las sesiones por mes" o "¿Cuántas sesiones hay por tienda?"',
      timestamp: new Date(Date.now() + 1000),
      id: '2'
    }
  ])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 100)
  }

  useEffect(() => {
    scrollToBottom()
  }, [msgs])

  const handleSend = async () => {
    if (!input.trim() || isLoading) return
    
    const userMessage: ChatMessage = {
      from: 'user',
      text: input,
      timestamp: new Date(),
      id: Date.now().toString()
    }
    
    setMsgs((prev) => [...prev, userMessage])
    setInput('')
    setIsLoading(true)

    try {
        console.log('Enviando consulta:', input)
      const { text, chartSpec } = await sendQuery(input)
      
      const botMessage: ChatMessage = {
        from: 'bot',
        text,
        timestamp: new Date(),
        id: (Date.now() + 1).toString()
      }
      
      setMsgs((prev) => [...prev, botMessage])
      onNewChart(chartSpec)
    } catch (err) {
      const errorMessage: ChatMessage = {
        from: 'bot',
        text: 'Lo siento, no pude procesar tu consulta. ¿Podrías intentar reformularla?',
        timestamp: new Date(),
        id: (Date.now() + 1).toString()
      }
      setMsgs((prev) => [...prev, errorMessage])
      console.error(err)
    } finally {
      setIsLoading(false)
    }
  }

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('es-ES', { 
      hour: '2-digit', 
      minute: '2-digit' 
    })
  }

  return (
    <div style={{ 
      width: 350, 
      height: '90vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#f0f2f5',
      borderRight: '1px solid #e0e0e0'
    }}>
      {/* Header */}
      <div style={{
        backgroundColor: '#075e54',
        color: 'white',
        padding: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          backgroundColor: '#128c7e',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px',
          fontWeight: 'bold'
        }}>
          📊
        </div>
        <div>
          <div style={{ fontWeight: 'bold', fontSize: '16px' }}>Asistente IA</div>
          <div style={{ fontSize: '12px', opacity: 0.8 }}>Estadísticas en tiempo real</div>
        </div>
      </div>

      {/* Messages Container */}
      <div style={{ 
        flex: 1, 
        overflowY: 'auto', 
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
      }}>
        {msgs.map((message) => (
          <div
            key={message.id}
            style={{
              display: 'flex',
              justifyContent: message.from === 'user' ? 'flex-end' : 'flex-start',
              marginBottom: '8px'
            }}
          >
            <div style={{
              maxWidth: '70%',
              padding: '12px 16px',
              borderRadius: message.from === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
              backgroundColor: message.from === 'user' ? '#dcf8c6' : 'white',
              color: '#303030',
              fontSize: '14px',
              lineHeight: '1.4',
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
              position: 'relative'
            }}>
              <div style={{ marginBottom: '4px' }}>{message.text}</div>
              <div style={{
                fontSize: '11px',
                color: '#667781',
                textAlign: message.from === 'user' ? 'right' : 'left',
                marginTop: '4px'
              }}>
                {formatTime(message.timestamp)}
              </div>
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div style={{
            display: 'flex',
            justifyContent: 'flex-start',
            marginBottom: '8px'
          }}>
            <div style={{
              padding: '12px 16px',
              borderRadius: '18px 18px 18px 4px',
              backgroundColor: 'white',
              color: '#303030',
              fontSize: '14px',
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <div style={{
                display: 'flex',
                gap: '4px'
              }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#667781',
                  animation: 'typing 1.4s infinite ease-in-out'
                }}></div>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#667781',
                  animation: 'typing 1.4s infinite ease-in-out 0.2s'
                }}></div>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#667781',
                  animation: 'typing 1.4s infinite ease-in-out 0.4s'
                }}></div>
      </div>
              <span style={{ fontSize: '11px', color: '#667781' }}>Escribiendo...</span>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div style={{
        padding: '16px',
        backgroundColor: '#f0f2f5',
        borderTop: '1px solid #e0e0e0'
      }}>
        <div style={{
          display: 'flex',
          gap: '8px',
          alignItems: 'flex-end'
        }}>
          <div style={{
            flex: 1,
            backgroundColor: 'white',
            borderRadius: '20px',
            padding: '8px 16px',
            border: '1px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
      <input
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                fontSize: '14px',
                backgroundColor: 'transparent',
                color: '#303030'
              }}
        value={input}
        onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
              placeholder="Escribe tu consulta..."
              disabled={isLoading}
            />
            <button
              style={{
                background: 'none',
                border: 'none',
                color: '#667781',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f0f2f5'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent'
              }}
              title="Grabar audio (próximamente)"
            >
              <MicrophoneIcon />
            </button>
          </div>
          <button
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: input.trim() && !isLoading ? '#25d366' : '#cccccc',
              border: 'none',
              color: 'white',
              fontSize: '18px',
              cursor: input.trim() && !isLoading ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
              boxShadow: input.trim() && !isLoading ? '0 2px 4px rgba(37, 211, 102, 0.3)' : 'none'
            }}
            onMouseEnter={(e) => {
              if (input.trim() && !isLoading) {
                e.currentTarget.style.transform = 'scale(1.05)'
              }
            }}
            onMouseLeave={(e) => {
              if (input.trim() && !isLoading) {
                e.currentTarget.style.transform = 'scale(1)'
              }
            }}
          >
            <SendIcon />
          </button>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes typing {
            0%, 60%, 100% {
              transform: translateY(0);
              opacity: 0.4;
            }
            30% {
              transform: translateY(-10px);
              opacity: 1;
            }
          }
          
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateX(-20px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          
          .chart-container {
            animation: fadeInUp 0.6s ease-out;
          }
          
          .chart-wrapper {
            transition: all 0.3s ease;
          }
          
          .chart-wrapper:hover {
            transform: translateY(-4px);
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          }
          
          .chart-header {
            animation: slideIn 0.4s ease-out 0.2s both;
          }
          
          .chart-content {
            animation: fadeInUp 0.6s ease-out 0.4s both;
          }
          
          .chart-footer {
            animation: slideIn 0.4s ease-out 0.6s both;
          }
        `
      }} />
    </div>
  )
}

// Paleta de colores moderna y profesional
const MODERN_COLORS = [
  '#6366f1', // Indigo
  '#8b5cf6', // Violet
  '#06b6d4', // Cyan
  '#10b981', // Emerald
  '#f59e0b', // Amber
  '#ef4444', // Red
  '#ec4899', // Pink
  '#84cc16', // Lime
  '#f97316', // Orange
  '#06b6d4'  // Sky
]

// Gradientes modernos
const GRADIENTS = {
  primary: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  secondary: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  success: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  warning: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  danger: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  info: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)'
}

// 5. DynamicChart - Diseño Moderno
  interface DynamicChartProps {
    spec: ChartSpec
    data: any[]
  }

  function DynamicChart({ spec, data }: DynamicChartProps) {
    const theme = useTheme()
  
  const dimMap: Record<string, string> = {
    month: 'Mes',
    day: 'Día',
    store: 'Tienda',
    storeId: 'Tienda',
    affectedZone: 'Zona afectada',
    completed: 'Asistencia',
    process: 'Proceso',
    gender: 'Sexo',
    sector: 'Sector',
    role: 'Puesto'
  }
  
       const metricLabel = spec.metric === 'Sesiones' ? 'Número de sesiones' : 'Tiempo total (min)'
       const dimensionLabel = dimMap[spec.dimension] || spec.dimension
       const title = `${metricLabel} por ${dimensionLabel}`

  // Chart Wrapper Moderno
  const ModernChartWrapper: React.FC<{children: React.ReactElement}> = ({ children }) => (
    <div className="chart-container chart-wrapper" style={{
      background: 'white',
      borderRadius: '20px',
      padding: '32px',
      margin: '16px',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      backdropFilter: 'blur(10px)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Header del gráfico */}
      <div className="chart-header" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '24px',
        paddingBottom: '16px',
        borderBottom: '2px solid #f1f5f9'
      }}>
        <div>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#1e293b',
            margin: '0 0 4px 0',
            background: GRADIENTS.primary,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            {title}
          </h2>
          <p style={{
            fontSize: '14px',
            color: '#64748b',
            margin: '0',
            fontWeight: '500'
          }}>
            Datos actualizados en tiempo real
          </p>
        </div>
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '12px',
          background: GRADIENTS.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '20px',
          color: 'white',
          fontWeight: 'bold'
        }}>
          📊
        </div>
      </div>
      
      {/* Contenido del gráfico */}
      <div className="chart-content" style={{
        position: 'relative',
        height: spec.chartType === 'pie' ? '500px' : '400px'
      }}>
         {children}
      </div>
      
      {/* Footer con estadísticas */}
      <div className="chart-footer" style={{
        marginTop: '24px',
        paddingTop: '16px',
        borderTop: '1px solid #f1f5f9',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{
          display: 'flex',
          gap: '24px'
        }}>
          <div>
            <div style={{
              fontSize: '12px',
              color: '#64748b',
              fontWeight: '500',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Total de datos
            </div>
            <div style={{
              fontSize: '18px',
              fontWeight: '700',
              color: '#1e293b'
            }}>
              {data.length}
            </div>
          </div>
          <div>
            <div style={{
              fontSize: '12px',
              color: '#64748b',
              fontWeight: '500',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Tipo de gráfico
            </div>
            <div style={{
              fontSize: '18px',
              fontWeight: '700',
              color: '#1e293b'
            }}>
              {spec.chartType === 'line' ? 'Línea' : spec.chartType === 'bar' ? 'Barras' : 'Circular'}
            </div>
          </div>
        </div>
        <div style={{
          padding: '8px 16px',
          borderRadius: '20px',
          background: '#f8fafc',
          fontSize: '12px',
          color: '#64748b',
          fontWeight: '500'
        }}>
          Generado por IA
        </div>
      </div>
    </div>
  )

  // Custom Tooltip Moderno
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          background: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          borderRadius: '16px',
          padding: '20px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          fontSize: '14px',
          minWidth: '200px',
          position: 'relative'
        }}>
          {/* Header del tooltip */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '12px',
            paddingBottom: '8px',
            borderBottom: '1px solid #f1f5f9'
          }}>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: GRADIENTS.primary
            }}></div>
            <span style={{
              fontWeight: '600',
              color: '#1e293b',
              fontSize: '16px'
            }}>
              {label}
            </span>
          </div>
          
          {/* Contenido del tooltip */}
          {payload.map((entry: any, index: number) => (
            <div key={index} style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '8px',
              padding: '8px 12px',
              borderRadius: '8px',
              background: '#f8fafc'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <div style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  background: entry.color || MODERN_COLORS[index % MODERN_COLORS.length]
                }}></div>
                <span style={{
                  color: '#64748b',
                  fontWeight: '500'
                }}>
                  {entry.name}:
                </span>
              </div>
              <span style={{
                fontWeight: '700',
                color: '#1e293b',
                fontSize: '16px'
              }}>
                {entry.value}
              </span>
            </div>
          ))}
          
          {/* Footer del tooltip */}
          <div style={{
            marginTop: '12px',
            paddingTop: '8px',
            borderTop: '1px solid #f1f5f9',
            fontSize: '11px',
            color: '#94a3b8',
            textAlign: 'center'
          }}>
            Hover para más detalles
          </div>
        </div>
      )
    }
    return null
  }

console.log(spec, data)
  
  switch (spec.chartType) {
    case 'line':
      return (
        <ModernChartWrapper>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
          <defs>
                <linearGradient id="modernGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0.1}/>
                </linearGradient>
                <linearGradient id="lineGradient" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#6366f1"/>
                  <stop offset="100%" stopColor="#8b5cf6"/>
            </linearGradient>
          </defs>
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke="#e2e8f0"
                strokeOpacity={0.5}
              />
              <XAxis 
                dataKey={spec.dimension} 
                tick={{ 
                  fontSize: 12,
                  fill: '#64748b',
                  fontWeight: '500'
                }}
                axisLine={{ stroke: '#e2e8f0' }}
                tickLine={{ stroke: '#e2e8f0' }}
              />
              <YAxis 
                tick={{ 
                  fontSize: 12,
                  fill: '#64748b',
                  fontWeight: '500'
                }}
                axisLine={{ stroke: '#e2e8f0' }}
                tickLine={{ stroke: '#e2e8f0' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line 
                type="monotone" 
                dataKey={spec.metric}
                stroke="url(#lineGradient)"
                strokeWidth={4}
                fill="url(#modernGradient)"
                fillOpacity={0.3}
                activeDot={{ 
                  r: 8,
                  stroke: '#6366f1',
                  strokeWidth: 2,
                  fill: 'white'
                }}
                animationDuration={1500}
                animationEasing="ease-out"
          />
        </LineChart>
          </ResponsiveContainer>
        </ModernChartWrapper>
      )

    case 'bar':
      return (
        <ModernChartWrapper>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
              <defs>
                <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366f1"/>
                  <stop offset="100%" stopColor="#8b5cf6"/>
                </linearGradient>
              </defs>
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke="#e2e8f0"
                strokeOpacity={0.5}
              />
              <XAxis 
                dataKey={['month', 'day'].includes(spec.dimension) ? spec.dimension : 'name'} 
                tick={{ 
                  fontSize: 12,
                  fill: '#64748b',
                  fontWeight: '500'
                }}
                axisLine={{ stroke: '#e2e8f0' }}
                tickLine={{ stroke: '#e2e8f0' }}
              />
              <YAxis 
                tick={{ 
                  fontSize: 12,
                  fill: '#64748b',
                  fontWeight: '500'
                }}
                axisLine={{ stroke: '#e2e8f0' }}
                tickLine={{ stroke: '#e2e8f0' }}
              />
              <Tooltip content={<CustomTooltip />} />
            <Bar
              dataKey={['month', 'day'].includes(spec.dimension) ? spec.metric : 'value'}
                fill="url(#barGradient)"
                radius={[8, 8, 0, 0]}
                barSize={60}
                animationDuration={1200}
                animationEasing="ease-out"
            />
          </BarChart>
          </ResponsiveContainer>
        </ModernChartWrapper>
      )

    case 'pie':
    return (
        <ModernChartWrapper>
          <ResponsiveContainer width="100%" height="100%">
          <PieChart>
              <defs>
                {MODERN_COLORS.map((color, index) => (
                  <linearGradient key={index} id={`pieGradient${index}`} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity={0.8}/>
                    <stop offset="100%" stopColor={color} stopOpacity={1}/>
                  </linearGradient>
                ))}
              </defs>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
                innerRadius={80}
                outerRadius={160}
                paddingAngle={6}
                label={({ name, percent }) => (
                  `${name} ${(percent * 100).toFixed(0)}%`
                )}
              labelLine={false}
                animationDuration={1500}
                animationEasing="ease-out"
            >
              {data.map((_, idx) => (
                  <Cell 
                    key={idx} 
                    fill={`url(#pieGradient${idx % MODERN_COLORS.length})`}
                  />
              ))}
            </Pie>
              <Tooltip content={<CustomTooltip />} />
          </PieChart>
          </ResponsiveContainer>
        </ModernChartWrapper>
    )

    default:
      return (
        <ModernChartWrapper>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: '#64748b',
            fontSize: '16px'
          }}>
            Tipo de gráfico no soportado
          </div>
        </ModernChartWrapper>
      )
  }
}

// 6. Componente principal
// export default function Statistics() {
//   const [allBookings, setAllBookings] = useState<Booking[]>([])
//   const [spec, setSpec] = useState<ChartSpec | null>(null)
//   const [data, setData] = useState<any[]>([])

//   useEffect(() => {
//     fetchAllBookings().then(setAllBookings)
//   }, [])

//   const handleNewSpec = (newSpec: ChartSpec) => {
//     console.log('Nueva especificación recibida:', newSpec)
//     setSpec(newSpec)
//     const filtered = filterBookings(allBookings, newSpec.filters)
//     console.log('Datos filtrados:', filtered)
//     // const result = newSpec.chartType === 'pie' ? aggregatePie(filtered, newSpec) : aggregateTimeSeries(filtered, newSpec)
//     // setData(result)
//     let data
//     if (newSpec.chartType === 'line' || (newSpec.chartType === 'bar' && (newSpec.dimension === 'month' || newSpec.dimension === 'day'))) {
//       data = aggregateTimeSeries(filtered, newSpec)
//     } else {
//       // Para cualquier dimensión categórica (inclusive 'store'), usamos aggregateByCategory
//       data = aggregateByCategory(filtered, newSpec)
//     }
//     console.log('Datos agregados:', data)
//     setData(data)
//   }

//   return (
//     <div style={{ display: 'flex' }}>
//       <ChatPanel onNewChart={handleNewSpec} />
//       <div style={{ flex: 1, padding: 20 }}>
//         {spec ? <DynamicChart spec={spec} data={data} /> : <p>Escribe una consulta para ver estadísticas</p>}
//       </div>
//     </div>
//   )
// }

export default function Statistics() {
  const [spec, setSpec] = useState<ChartSpec | null>(null)
  const [data, setData] = useState<any[]>([])
  console.log(spec, data)
  // const handleSpec = async (newSpec: ChartSpec) => {
  //   console.log('Nueva especificación recibida:', newSpec)
  //   setSpec(newSpec)
  //   const bookings = await fetchBookingsBySpec(newSpec)
  //   console.log('Datos obtenidos:', bookings)
  //   const result =
  //     newSpec.chartType === 'pie' ||
  //     (newSpec.chartType === 'bar' && ['store', 'affectedZone', 'completed', 'process', 'gender'].includes(newSpec.dimension))
  //       ? aggregateByCategory(bookings, newSpec)
  //       : aggregateTimeSeries(bookings, newSpec)
  //   console.log('Datos agregados:', result)
  //   setData(result)
   

  // }
  const handleSpec = async (newSpec: ChartSpec) => {
    // Si la dimensión es categórica, forzar gráfico de pastel
    const categoricalDims = ['store', 'storeId', 'affectedZone', 'completed', 'process', 'gender']
    if (categoricalDims.includes(newSpec.dimension)) {
      newSpec.chartType = 'pie'
    }
    setSpec(newSpec)
    const bookings = await fetchBookingsBySpec(newSpec)
    const result =
      newSpec.chartType === 'pie' ||
      (newSpec.chartType === 'bar' && ['store', 'affectedZone', 'completed', 'process', 'gender', 'sector', 'role'].includes(newSpec.dimension))
        ? aggregateByCategory(bookings, newSpec)
        : aggregateTimeSeries(bookings, newSpec)
    setData(result)
  }
console.log(spec, data)
  return (
    <div style={{ 
      display: 'flex', 
      height: '90vh',
      backgroundColor: '#f0f2f5'
    }}>
      <ChatPanel onNewChart={handleSpec} />
      <div style={{ 
        flex: 1, 
        padding: '24px',
        overflowY: 'auto',
        backgroundColor: '#f0f2f5'
      }}>
        {spec ? (
          <DynamicChart spec={spec} data={data} />
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            textAlign: 'center',
            color: '#667781'
          }}>
            <div style={{
              fontSize: '64px',
              marginBottom: '16px'
            }}>
              📊
            </div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              marginBottom: '8px',
              color: '#303030'
            }}>
              Bienvenido al Asistente de Estadísticas
            </h2>
            <p style={{
              fontSize: '16px',
              maxWidth: '400px',
              lineHeight: '1.5'
            }}>
              Escribe una consulta en el chat para comenzar a visualizar tus datos. 
              Puedes preguntar sobre sesiones, tiempo, tiendas, y más.
            </p>
            <div style={{
              marginTop: '24px',
              padding: '16px',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              maxWidth: '500px'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: 'bold',
                marginBottom: '12px',
                color: '#303030'
              }}>
                Ejemplos de consultas:
              </h3>
              <ul style={{
                textAlign: 'left',
                paddingLeft: '20px',
                margin: 0
              }}>
                <li style={{ marginBottom: '8px' }}>Muéstrame las sesiones por mes</li>
                <li style={{ marginBottom: '8px' }}>¿Cuántas sesiones hay por tienda?</li>
                <li style={{ marginBottom: '8px' }}>Distribución de pacientes por género</li>
                <li style={{ marginBottom: '8px' }}>Tiempo total por zona afectada</li>
                <li style={{ marginBottom: '8px' }}>Sesiones completadas vs no completadas</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

